import {
    ACH_PAYMENT_FORM_HELP_TEXT,
    MAX_PAYMENT_FILE_SIZE_MB,
    MAX_PROPERTIES_SCROLL,
    PAY_ADDR_INVALID_RING,
    PAYMENT_ADDR_FIELD_ORDER,
    PAYMENT_FILE_TYPES,
    PAYMENT_FORM_STATE_KEYS,
} from '../constants'
import type {
    CompanyPaymentTabData,
    PaymentAddrFieldKey,
    PaymentFormState,
    PaymentStaffOption,
    PaymentUploadedFile,
    PropertyPaymentItem,
} from '../types'

/** @deprecated Prefer PAYMENT_FILE_TYPES from constants — kept for existing imports. */
export const FILE_TYPES = PAYMENT_FILE_TYPES
/** @deprecated Prefer MAX_PAYMENT_FILE_SIZE_MB from constants. */
export const MAX_FILE_SIZE_MB = MAX_PAYMENT_FILE_SIZE_MB
export {
    ACH_PAYMENT_FORM_HELP_TEXT,
    MAX_PROPERTIES_SCROLL,
    PAY_ADDR_INVALID_RING,
    PAYMENT_ADDR_FIELD_ORDER,
}

/** Geocode requires street, city, state, zip — matches Angular `validatePaymentAddress` gate. */
export function getMissingPaymentAddrFields(
    form: PaymentFormState,
): Partial<Record<PaymentAddrFieldKey, true>> {
    const missing: Partial<Record<PaymentAddrFieldKey, true>> = {}
    if (!String(form.payment_address ?? '').trim()) missing.payment_address = true
    if (!String(form.payment_city ?? '').trim()) missing.payment_city = true
    if (!String(form.payment_state ?? '').trim()) missing.payment_state = true
    if (!String(form.payment_zipcode ?? '').trim()) missing.payment_zipcode = true
    return missing
}

export function paymentAddrFieldTestId(
    field: PaymentAddrFieldKey,
    scope: 'company' | 'property',
    propertyId?: number | string,
): string {
    if (scope === 'company') {
        const map: Record<PaymentAddrFieldKey, string> = {
            payment_address: 'company-payments-paper-street-address-textarea',
            payment_city: 'company-payments-paper-city-input',
            payment_state: 'company-payments-paper-state-select',
            payment_zipcode: 'company-payments-paper-zipcode-input',
        }
        return map[field]
    }
    const pid = propertyId ?? ''
    const map: Record<PaymentAddrFieldKey, string> = {
        payment_address: `company-payments-property-${pid}-paper-street-address-textarea`,
        payment_city: `company-payments-property-${pid}-paper-city-input`,
        payment_state: `company-payments-property-${pid}-paper-state-select`,
        payment_zipcode: `company-payments-property-${pid}-paper-zip-input`,
    }
    return map[field]
}

function str(v: unknown): string {
    if (v === undefined || v === null) return ''
    return typeof v === 'string' ? v : String(v)
}

function optionalStr(v: unknown): string {
    return v !== undefined && v !== null ? String(v) : ''
}

function readStr(obj: Record<string, unknown> | null | undefined, snake: string, camel?: string): string {
    if (!obj) return ''
    const raw = camel != null && camel !== '' ? (obj[snake] ?? obj[camel]) : obj[snake]
    return str(raw)
}

export function serverSaysUseCompanyPaymentForAllProperties(
    company: CompanyPaymentTabData | null | undefined,
): boolean {
    if (!company) return false
    return (
        String(company['verify'] ?? '') === '1' && String(company['payment_for_property'] ?? '') === '1'
    )
}

export function normalizePaymentMethodToProperty(x: CompanyPaymentTabData): string {
    const v = x['payment_method_to_property'] ?? x['payment_for_property']
    if (v === undefined || v === null || v === '') return ''
    if (v === true || v === 1 || v === '1') return '1'
    if (v === false || v === 0 || v === '0') return '0'
    return String(v).trim()
}

export function normalizePaymentFiles(raw: unknown): PaymentUploadedFile[] {
    if (raw == null) return []
    if (Array.isArray(raw)) return raw as PaymentUploadedFile[]
    if (typeof raw === 'object') return Object.values(raw as Record<string, PaymentUploadedFile>)
    return []
}

export function propertyPaymentRows(company: CompanyPaymentTabData | null | undefined): CompanyPaymentTabData[] {
    const raw = company?.['property_payment']
    if (!Array.isArray(raw)) return []
    return raw as CompanyPaymentTabData[]
}

export function getFilterValue(params: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const key of Object.keys(params)) {
        if (!Object.prototype.hasOwnProperty.call(params, key)) continue
        let val = params[key]
        if (typeof val === 'string') val = val.trim()
        if (val !== undefined && val !== null && val !== '') result[key] = val
    }
    return result
}

export function buildPaymentFormState(c: CompanyPaymentTabData | null | undefined): PaymentFormState {
    const x = c ?? {}
    return {
        payment_method: str(x['payment_method']),
        verify: optionalStr(x['verify']),
        payment_method_to_property: normalizePaymentMethodToProperty(x),
        routing_number: readStr(x as Record<string, unknown>, 'routing_number', 'routingNumber'),
        account_number: readStr(x as Record<string, unknown>, 'account_number', 'accountNumber'),
        property_method: str(x['property_method']) || 'Checkings',
        company_status: str(x['company_status']),
        payee_name: str(x['payee_name']),
        payment_address: str(x['payment_address']),
        payment_shop: str(x['payment_shop']),
        payment_city: str(x['payment_city']),
        payment_state: str(x['payment_state']),
        payment_zipcode: str(x['payment_zipcode']),
        other_payee_name: str(x['other_payee_name']),
    }
}

/** Compare local company payment form values (Angular `companyEditForm` payment controls). */
export function paymentFormStatesEqual(a: PaymentFormState, b: PaymentFormState): boolean {
    return PAYMENT_FORM_STATE_KEYS.every((key) => String(a[key] ?? '') === String(b[key] ?? ''))
}

/**
 * Angular `updateAllProperty()`: `companyEditForm.dirty` — skip `editCompany('updateproperty')`
 * when payment fields match GET snapshot (only `savePropertyInfo` / bulk API runs).
 * Pass `paymentMethodToPropertyOverride` for the pending Yes/No toggle (React holds it until confirm).
 */
export function isCompanyPaymentFormDirty(
    form: PaymentFormState,
    company: CompanyPaymentTabData | null | undefined,
    paymentMethodToPropertyOverride?: '0' | '1',
): boolean {
    const saved = buildPaymentFormState(company)
    const effective: PaymentFormState =
        paymentMethodToPropertyOverride !== undefined
            ? { ...form, payment_method_to_property: paymentMethodToPropertyOverride }
            : form
    return !paymentFormStatesEqual(effective, saved)
}

export function buildPropertyPaymentItem(
    p: CompanyPaymentTabData,
    company: CompanyPaymentTabData | null | undefined,
    companyForm?: PaymentFormState | null,
): PropertyPaymentItem {
    if (company == null) {
        return {
            ...buildPaymentFormState(p),
            property_id: Number(p['property_id']) || 0,
            property_name: str(p['property_name']),
            pay_to: str(p['pay_to']),
        }
    }

    const noRegex = /^-?\d+$/
    let payment_address = str(p['payment_address'])
    let payment_city = str(p['payment_city'])
    let payment_state = str(p['payment_state'])
    let payment_zipcode = str(p['payment_zipcode'])
    if (!String(payment_address ?? '').trim() && !String(payment_city ?? '').trim()) {
        payment_address = str(p['property_address'])
        payment_city = str(p['property_city'])
        payment_state = str(p['property_state'])
        payment_zipcode = str(p['property_zipcode'])
    }

    let pay_to = str(p['pay_to'])
    let verify = optionalStr(p['verify'])
    let payment_method = str(p['payment_method'])
    let routing_number = readStr(p as Record<string, unknown>, 'routing_number', 'routingNumber')
    let account_number = readStr(p as Record<string, unknown>, 'account_number', 'accountNumber')
    let property_method = str(p['property_method']) || 'Checkings'
    let company_status = str(p['company_status'])
    let payee_name = str(p['payee_name'])
    let payment_shop = str(p['payment_shop'])
    let other_payee_name = str(p['other_payee_name'])

    if (serverSaysUseCompanyPaymentForAllProperties(company)) {
        pay_to = 'COMPANY'
        verify = optionalStr(companyForm?.verify ?? company['verify'])
        payment_method = str(company['payment_method'])
        routing_number = readStr(company as Record<string, unknown>, 'routing_number', 'routingNumber')
        account_number = readStr(company as Record<string, unknown>, 'account_number', 'accountNumber')
        property_method = str(company['property_method']) || 'Checkings'
        company_status = str(company['company_status'])
        payee_name = str(company['payee_name'])
        payment_shop = str(company['payment_shop'])
        other_payee_name = str(company['other_payee_name'])

        if (!str(company['payment_address']).trim() && !str(company['payment_city']).trim()) {
            payment_address = str(company['company_address']) || payment_address
            payment_city = str(company['company_city']) || payment_city
            payment_state = str(company['company_state']) || payment_state
            payment_zipcode = str(company['company_zipcode']) || payment_zipcode
        } else {
            const addrSrc = companyForm ?? buildPaymentFormState(company)
            payment_address = str(addrSrc.payment_address) || str(company['payment_address']) || payment_address
            payment_shop = str(addrSrc.payment_shop) || str(company['payment_shop']) || payment_shop
            payment_city = str(addrSrc.payment_city) || str(company['payment_city']) || payment_city
            payment_state = str(addrSrc.payment_state) || str(company['payment_state']) || payment_state
            payment_zipcode = str(addrSrc.payment_zipcode) || str(company['payment_zipcode']) || payment_zipcode
        }
    } else {
        pay_to = 'PROPERTY'
        verify = optionalStr(p['verify'])
    }

    if (pay_to === 'COMPANY') {
        const pm = str(companyForm?.payment_method ?? company['payment_method'])
        if (pm.trim() !== '') payment_method = pm
    }

    if (!property_method) {
        property_method = 'Checkings'
    }

    if (payee_name && payee_name !== 'LEASING' && !noRegex.test(String(payee_name))) {
        other_payee_name = String(payee_name)
        payee_name = 'OTHER'
    }

    return {
        payment_method,
        verify,
        payment_method_to_property: normalizePaymentMethodToProperty(company),
        routing_number,
        account_number,
        property_method,
        company_status,
        payee_name,
        payment_address,
        payment_shop,
        payment_city,
        payment_state,
        payment_zipcode,
        other_payee_name,
        property_id: Number(p['property_id']) || 0,
        property_name: str(p['property_name']),
        pay_to,
    }
}

export function getPropertyPayeeStaffForDropdown(
    company: CompanyPaymentTabData | null | undefined,
    index: number,
    useCompanyStaff: boolean,
): PaymentStaffOption[] {
    if (useCompanyStaff) return (company?.['company_staff'] as PaymentStaffOption[] | undefined) ?? []
    const rows = propertyPaymentRows(company)
    const row = rows[index]
    const raw = row?.['property_staff']
    const list = Array.isArray(raw) ? (raw as PaymentStaffOption[]) : []
    if (list.length === 0 && serverSaysUseCompanyPaymentForAllProperties(company)) {
        return (company?.['company_staff'] as PaymentStaffOption[] | undefined) ?? []
    }
    return list
}

export function getPropertyRowAfterPayToChange(
    index: number,
    newPayTo: string,
    company: CompanyPaymentTabData | null | undefined,
    companyForm: PaymentFormState,
): Partial<PropertyPaymentItem> {
    const propertyData = propertyPaymentRows(company)[index]
    if (!propertyData) {
        return { pay_to: newPayTo }
    }

    const noRegex = /^-?\d+$/
    let base = buildPaymentFormState(propertyData)
    base = {
        ...base,
        payment_address: str(propertyData['property_address']) || base.payment_address,
        payment_city: str(propertyData['property_city']) || base.payment_city,
        payment_state: str(propertyData['property_state']) || base.payment_state,
        payment_zipcode: str(propertyData['property_zipcode']) || base.payment_zipcode,
    }

    if (serverSaysUseCompanyPaymentForAllProperties(company) || newPayTo === 'COMPANY') {
        const merged = buildPaymentFormState(company)
        merged.payment_method = str(companyForm.payment_method ?? merged.payment_method)
        merged.property_method = str(companyForm.property_method ?? merged.property_method) || 'Checkings'
        merged.verify = optionalStr(companyForm.verify ?? merged.verify)
        const patch: Partial<PropertyPaymentItem> = {
            ...merged,
            property_name: str(propertyData['property_name']),
            property_id: Number(propertyData['property_id']) || 0,
        }
        if (!str(company?.['payment_address']).trim() && !str(company?.['payment_city']).trim()) {
            patch.payment_address = str(company?.['company_address'])
            patch.payment_city = str(company?.['company_city'])
            patch.payment_state = str(company?.['company_state'])
            patch.payment_zipcode = str(company?.['company_zipcode'])
        } else {
            patch.payment_address = companyForm.payment_address
            patch.payment_shop = companyForm.payment_shop
            patch.payment_city = companyForm.payment_city
            patch.payment_state = companyForm.payment_state
            patch.payment_zipcode = companyForm.payment_zipcode
        }
        let payee_name = patch.payee_name ?? ''
        let other_payee_name = patch.other_payee_name ?? ''
        if (payee_name && payee_name !== 'LEASING' && !noRegex.test(String(payee_name))) {
            other_payee_name = String(payee_name)
            payee_name = 'OTHER'
        }
        patch.payee_name = payee_name
        patch.other_payee_name = other_payee_name
        if (!patch.property_method) patch.property_method = 'Checkings'
        patch.pay_to = newPayTo
        return patch
    }

    let payee_name = base.payee_name ?? ''
    let other_payee_name = base.other_payee_name ?? ''
    if (payee_name && payee_name !== 'LEASING' && !noRegex.test(String(payee_name))) {
        other_payee_name = String(payee_name)
        payee_name = 'OTHER'
    }
    return {
        ...base,
        pay_to: newPayTo,
        payee_name,
        other_payee_name,
        property_method: base.property_method || 'Checkings',
        property_name: str(propertyData['property_name']),
        property_id: Number(propertyData['property_id']) || 0,
    }
}

export function shouldShowPropertyAchRow(prop: PropertyPaymentItem, companyPaymentMethod: string): boolean {
    return (
        (prop.payment_method === 'ACH' && prop.pay_to !== 'COMPANY') ||
        (prop.pay_to === 'COMPANY' && companyPaymentMethod === 'ACH')
    )
}

export function shouldShowPropertyPaperRow(prop: PropertyPaymentItem, companyPaymentMethod: string): boolean {
    return (
        (prop.payment_method === 'PAPER_CHECK' && prop.pay_to !== 'COMPANY') ||
        (prop.pay_to === 'COMPANY' && companyPaymentMethod === 'PAPER_CHECK')
    )
}

export function resolveCompanyId(
    companyIdFromRoute: string | undefined,
    company: CompanyPaymentTabData | null | undefined,
): string {
    if (companyIdFromRoute != null && String(companyIdFromRoute).trim() !== '') {
        return String(companyIdFromRoute).trim()
    }
    const fromApi = company?.['company_id'] ?? company?.['id']
    return fromApi != null && String(fromApi).trim() !== '' ? String(fromApi) : ''
}
